/**
 * Layout Component - Shared header and footer
 */

import type { ReactNode } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigationStore } from '../store/useNavigationStore';
import { InstallPrompt } from './InstallPrompt';
import { LanguageSelector } from './LanguageSelector';
import { SignInModal } from './SignInModal';
import './Layout.css';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { t } = useTranslation();
  const { navigate, currentPage } = useNavigationStore();
  const { isRegistered, email } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);

  const handleNavigate = (page: 'home' | 'imprint' | 'privacy') => {
    navigate(page);
  };

  return (
    <div className="layout">
      <header className="layout-header">
        <div className="header-left">
          <button
            type="button"
            onClick={() => handleNavigate('home')}
            className="logo-button"
            aria-label="Go to home"
          >
            <h1>🎤 Refinevoice</h1>
          </button>
          {currentPage === 'home' && <p className="tagline">{t('layout.tagline')}</p>}
        </div>
        <div className="header-right">
          {isRegistered ? (
            <button
              type="button"
              className="user-status header-user"
              title={`Signed in as ${email} - Click to manage account`}
              onClick={() => navigate('account')}
            >
              👤 {email}
            </button>
          ) : (
            <button
              type="button"
              className="btn-icon btn-signin header-signin"
              onClick={() => setShowSignIn(true)}
              title="Sign in for longer voice recordings"
            >
              👤 Sign In
            </button>
          )}
          <LanguageSelector />
        </div>
      </header>

      <main className="layout-main">{children}</main>

      <InstallPrompt />

      <footer className="layout-footer">
        <nav className="footer-nav">
          <button
            type="button"
            onClick={() => handleNavigate('imprint')}
            className={currentPage === 'imprint' ? 'active' : ''}
          >
            {t('layout.impressum')}
          </button>
          <span className="separator">·</span>
          <button
            type="button"
            onClick={() => handleNavigate('privacy')}
            className={currentPage === 'privacy' ? 'active' : ''}
          >
            {t('layout.datenschutz')}
          </button>
          <span className="separator">·</span>
          <a href="mailto:info@refinevoice.com">info@refinevoice.com</a>
        </nav>
      </footer>

      {/* Sign In Modal */}
      {showSignIn && <SignInModal isOpen={showSignIn} onClose={() => setShowSignIn(false)} />}
    </div>
  );
}
